import {
  useCallback,
  useEffect,
  useState,
  type FC,
  type RefObject,
} from "react";
import { twMerge } from "tailwind-merge";

/** Height of the fade overlay at the top/bottom of the scroll region. */
const SCROLL_EDGE_FADE_HEIGHT_CLASS = "h-8";

const SCROLL_EDGE_THRESHOLD_PX = 4;

export interface ScrollEdgeFadesProps {
  /** The scrollable element the fades overlay (position it relatively). */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** The growing content inside it, observed so streamed growth updates the fades. */
  scrollContentRef: RefObject<HTMLElement | null>;
}

/**
 * Top/bottom gradient overlays that only appear when content is scrolled
 * under the corresponding edge — never when flush to the top or bottom.
 * Render inside a `relative` wrapper around the scroll container.
 */
export const ScrollEdgeFades: FC<ScrollEdgeFadesProps> = ({
  scrollContainerRef,
  scrollContentRef,
}) => {
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const updateEdgeFades = useCallback(() => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement) {
      setShowTopFade(false);
      setShowBottomFade(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollElement;
    const canScroll = scrollHeight > clientHeight + SCROLL_EDGE_THRESHOLD_PX;

    setShowTopFade(canScroll && scrollTop > SCROLL_EDGE_THRESHOLD_PX);
    setShowBottomFade(
      canScroll &&
        scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_THRESHOLD_PX,
    );
  }, [scrollContainerRef]);

  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement) {
      return;
    }

    updateEdgeFades();

    scrollElement.addEventListener("scroll", updateEdgeFades, {
      passive: true,
    });

    // Observing the content element (not a subtree MutationObserver) catches
    // streamed message growth without per-token layout thrash.
    const resizeObserver = new ResizeObserver(updateEdgeFades);
    resizeObserver.observe(scrollElement);
    if (scrollContentRef.current) {
      resizeObserver.observe(scrollContentRef.current);
    }

    return () => {
      scrollElement.removeEventListener("scroll", updateEdgeFades);
      resizeObserver.disconnect();
    };
  }, [scrollContainerRef, scrollContentRef, updateEdgeFades]);

  return (
    <>
      <div
        aria-hidden
        className={twMerge(
          "pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-card via-card/95 to-transparent transition-opacity duration-150 motion-reduce:transition-none",
          SCROLL_EDGE_FADE_HEIGHT_CLASS,
          showTopFade ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={twMerge(
          "pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-card via-card/95 to-transparent transition-opacity duration-150 motion-reduce:transition-none",
          SCROLL_EDGE_FADE_HEIGHT_CLASS,
          showBottomFade ? "opacity-100" : "opacity-0",
        )}
      />
    </>
  );
};
